import { useState, useEffect, useRef } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const ease: [number, number, number, number] = [0.16, 1, 0.3, 1]

interface LocationState {
  email: string
  username: string
  password: string
  full_name?: string
}

export function EmailVerificationPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { verifyOtp, sendOtp, signupWithVerification, isLoading } = useAuthStore()
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [timeLeft, setTimeLeft] = useState(600)
  const [canResend, setCanResend] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const reduced = useReducedMotion()
  const m = (d: number) => reduced ? undefined : { duration: 0.7, delay: d, ease }

  const state = location.state as LocationState

  useEffect(() => {
    if (!state?.email) {
      toast.error('Please start the signup process again')
      navigate('/signup')
      return
    }
  }, [state, navigate])

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCanResend(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasteData = e.clipboardData.getData('text').slice(0, 6)
    if (/^\d{6}$/.test(pasteData)) {
      const newOtp = pasteData.split('')
      setOtp(newOtp)
      inputRefs.current[5]?.focus()
    }
  }

  const handleVerifyOtp = async () => {
    const otpCode = otp.join('')

    if (otpCode.length !== 6) {
      toast.error('Please enter all 6 digits')
      return
    }

    try {
      await verifyOtp(state.email, otpCode)
      await signupWithVerification(state.username, state.email, state.password, state.full_name)
      localStorage.setItem('jpdz_first_time_user', 'true')
      toast.success('Email verified successfully! Welcome to JPDZ!')
      navigate('/chat')
    } catch {
      // Error handled in store
    }
  }

  const handleResendOtp = async () => {
    setIsResending(true)
    try {
      await sendOtp(state.email, state.username)
      setTimeLeft(600)
      setCanResend(false)
      setOtp(['', '', '', '', '', ''])
      toast.success('New verification code sent!')
      inputRefs.current[0]?.focus()
    } catch {
      toast.error('Failed to resend verification code')
    } finally {
      setIsResending(false)
    }
  }

  if (!state?.email) {
    return null
  }

  return (
    <div className="min-h-screen bg-[#211d18] text-[#eee8dc] flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(90deg, rgba(238,232,220,0.035) 1px, transparent 1px), linear-gradient(rgba(238,232,220,0.025) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={m(0)}
        className="relative z-10 w-full max-w-[420px] px-6"
      >
        {/* Brand */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={m(0.05)} className="mb-14 text-center">
          <Link to="/" className="font-serif text-2xl text-[#eee8dc] transition-colors duration-200 hover:text-[#c8a88a]">
            Jpdz Labs
          </Link>
        </motion.div>

        {/* Back */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={m(0.08)} className="mb-8">
          <Link to="/signup" className="group inline-flex items-center gap-2 font-body text-[13px] text-[#eee8dc]/30 transition-colors duration-200 hover:text-[#eee8dc]/60">
            <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-x-1" />
            Back to signup
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={m(0.1)} className="mb-10">
          <p className="mb-3 font-body text-[11px] uppercase tracking-[0.3em] text-[#c8a88a]/50">Verify</p>
          <h1 className="font-serif text-[clamp(2.4rem,5vw,3.5rem)] font-medium leading-[0.9]">
            Check your email.
          </h1>
          <p className="mt-5 font-body text-[15px] leading-[1.7] text-[#eee8dc]/30">
            We sent a 6-digit code to
          </p>
          <p className="mt-1 font-body text-[15px] text-[#c8a88a]">{state.email}</p>
        </motion.div>

        {/* OTP inputs */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={m(0.18)} className="mb-4">
          <div className="flex gap-3 justify-center">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="h-14 w-12 border-b-2 border-[#eee8dc]/10 bg-transparent text-center font-serif text-xl text-[#eee8dc] outline-none transition-colors duration-300 focus:border-[#c8a88a]/50"
              />
            ))}
          </div>
        </motion.div>

        {/* Timer */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={m(0.22)} className="mb-8 text-center">
          {timeLeft > 0 ? (
            <p className="font-body text-[13px] text-[#eee8dc]/20">
              Expires in <span className="text-[#c8a88a]/50">{formatTime(timeLeft)}</span>
            </p>
          ) : (
            <p className="font-body text-[13px] text-[#6e1f1b]">Code expired</p>
          )}
        </motion.div>

        {/* Verify button */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={m(0.26)}
          onClick={handleVerifyOtp}
          disabled={isLoading || otp.join('').length !== 6}
          className="group flex w-full items-center justify-center gap-3 bg-[#6e1f1b] px-6 py-4 font-body text-[14px] font-medium tracking-wide text-[#eee8dc] transition-all duration-300 hover:bg-[#8a2822] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <motion.div
              className="h-4 w-4 border-2 border-[#eee8dc]/20 border-t-[#eee8dc] rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
          ) : (
            'Verify Email'
          )}
        </motion.button>

        {/* Resend */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={m(0.3)} className="mt-8 text-center space-y-3">
          <p className="font-body text-[13px] text-[#eee8dc]/20">Didn't receive a code?</p>
          <button
            onClick={handleResendOtp}
            disabled={!canResend || isResending}
            className="font-body text-[13px] text-[#c8a88a]/40 transition-colors duration-200 hover:text-[#c8a88a] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {isResending ? 'Sending...' : 'Resend code'}
          </button>
        </motion.div>
      </motion.div>
    </div>
  )
}
